/**
 * Cart Normalizer
 * Standardizes backend cart payloads into a consistent internal schema.
 * Provides both camelCase and snake_case properties to ensure 100% backward
 * compatibility with existing UI components (ViewCart, ViewCartModal, Checkout, Product cards).
 */

export const normalizeCartResponse = (raw) => {
  if (!raw || raw.cart === null || (raw.message && raw.cart === null)) {
    return {
      cartId: null,
      version: 1,
      storeId: Number(typeof window !== "undefined" && window.localStorage ? window.localStorage.getItem("storeId") : 0) || 0,
      totalItems: 0,
      totalQuantity: 0,
      totalMrp: 0,
      totalDiscount: 0,
      total: 0,
      subtotal: 0,
      products: [],
    };
  }

  // Handle potential envelope wrapper: { status, data: { ... } }
  const data = raw.data && typeof raw.data === "object" && !Array.isArray(raw.data) ? raw.data : raw;

  const rawProducts = Array.isArray(data.products)
    ? data.products
    : Array.isArray(data.items)
    ? data.items
    : Array.isArray(data.order_details)
    ? data.order_details
    : Array.isArray(data.neworder_detail)
    ? data.neworder_detail
    : Array.isArray(data)
    ? data
    : [];

  let calculatedSubtotal = 0;
  let calculatedTotalMrp = 0;
  let calculatedTotalQty = 0;

  const normalizedProducts = rawProducts
    .filter((item) => {
      const q = Number(item.quantity ?? item.qty ?? item.min_qty ?? 1);
      return !isNaN(q) && q > 0;
    })
    .map((item) => {
    // Resolve product ID vs cart item ID
    const productId = item.product?.id ?? item.product_id ?? item.productId ?? item.id;
    const itemId = item.item_id ?? item.id ?? null;
    const name = item.product?.name ?? item.product_name ?? item.name ?? "";
    
    // Resolve image
    // Note: ViewCart.js line 284 requires `product.image ? product.image.image : product.imagef`
    const rawImage = item.product?.image ?? item.image ?? item.product?.featured_image ?? item.featured_image ?? null;
    const imageString = typeof rawImage === "string" 
      ? rawImage 
      : (rawImage?.image || item.imagef || item.featured_image || "");
    const imageObj = typeof rawImage === "object" && rawImage !== null 
      ? rawImage 
      : (imageString ? { image: imageString } : null);

    const price = Number(item.price ?? item.product?.price ?? item.selling_price ?? item.mrp ?? 0);
    const mrp = Number(item.mrp ?? item.product?.mrp ?? item.price ?? price);
    const offerPrice = Number(item.offer_price ?? item.offerPrice ?? item.product?.offer_price ?? 0);
    const quantity = Number(item.quantity ?? item.qty ?? item.min_qty ?? 1);
    const minQty = Number(item.min_qty ?? item.minQty ?? item.product?.min_qty ?? 1);
    const maxQty = Number(item.max_qty ?? item.maxQty ?? item.product?.max_qty ?? 999);
    const weightInKg = item.weight_in_kg ?? item.weight ?? item.product?.weight_in_kg ?? null;

    const baseUnit = offerPrice > 0 ? offerPrice : price;
    const variantPrice = Number(item.variant_Price ?? item.variantPrice ?? 0);
    const lineTotal = Number(
      item.line_total ??
      item.lineTotal ??
      item.total_amount ??
      ((baseUnit + variantPrice) * quantity)
    );

    calculatedSubtotal += lineTotal;
    calculatedTotalMrp += mrp * quantity;
    calculatedTotalQty += quantity;

    return {
      id: productId,
      productId,
      itemId,
      name,
      product_name: name,
      image: imageObj,
      imagef: imageString,
      featured_image: imageString,
      images: item.product?.images ?? item.images ?? [],
      weight_in_kg: weightInKg,
      weightInKg,
      price,
      selling_price: price,
      mrp,
      offer_price: offerPrice,
      offerPrice,
      quantity,
      qty: quantity,
      min_qty: minQty,
      minQty,
      max_qty: maxQty,
      maxQty,
      subcategory: item.subcategory ?? item.product?.subcategory ?? null,
      category: item.product?.category ?? item.category ?? null,
      speed: item.product?.speed ?? item.speed ?? item.product?.category ?? item.category ?? null,
      lineTotal,
      line_total: lineTotal,
      isAvailable: item.is_available ?? item.isAvailable ?? item.product?.is_available ?? true,
      is_available: item.is_available ?? item.isAvailable ?? item.product?.is_available ?? true,
      selectedPackage: item.selectedPackage ?? null,
      variant_Price: variantPrice,
    };
  });

  const summary = data.summary || {};
  const subtotal = Number(summary.subtotal ?? data.subtotal ?? data.total ?? data.total_amount ?? calculatedSubtotal);
  const total = Number(data.total ?? summary.subtotal ?? data.subtotal ?? calculatedSubtotal);
  const totalMrp = Number(summary.total_mrp ?? data.total_mrp ?? data.totalMrp ?? calculatedTotalMrp);
  const totalDiscount = Number(summary.total_discount ?? data.total_discount ?? data.totalDiscount ?? Math.max(0, totalMrp - total));
  const totalQuantity = Number(summary.total_quantity ?? calculatedTotalQty);
  const totalItems = Number(summary.total_items ?? normalizedProducts.length);

  return {
    cartId: data.id ?? data.cart_id ?? data.order_id ?? null,
    version: Number(data.version ?? 1),
    storeId: Number(data.stores ?? data.store_id ?? data.store ?? (typeof window !== "undefined" && window.localStorage ? window.localStorage.getItem("storeId") : 0) ?? 0),
    totalItems,
    totalQuantity,
    totalMrp,
    totalDiscount,
    total,
    subtotal,
    products: normalizedProducts,
  };
};

export default normalizeCartResponse;
